/* global React, window, PAPELARIA_CATALOG, PAPELARIA_PARTNERS */
const { useState, useMemo } = React;

// ================================================================
// Step Papelaria, entra ANTES da confirmação final.
// Splittado em DOIS steps no menu lateral:
//   - StepPapelariaIntro  (Ponto de partida)
//   - StepPapelariaBuilder (Montagem da lista)
// O usuário avança naturalmente entre eles via botão "Próximo".
// ================================================================

function StepPapelariaIntro() {
  const { data, setData } = useApp();
  const mode = data.papelariaMode;
  const set = (v) => setData(d => ({ ...d, papelariaMode: v }));
  if (mode === "skip") return <PapelariaSkipped onReopen={() => set(undefined)} />;
  return <PapelariaIntro onPick={set} currentMode={mode} />;
}

function StepPapelariaBuilder() {
  const { data, setData } = useApp();
  const mode = data.papelariaMode;
  if (!mode || mode === "skip") {
    return (
      <div>
        <Heading
          eyebrow="Papelaria · Passo 1, Montagem"
          title="Volte ao passo anterior pra escolher o ponto de partida"
          subtitle="Esta etapa só é necessária se você incluir papelaria na lista." />
        <LexBubble pose="wave">Se mudar de ideia, é só voltar pro passo anterior 🌱</LexBubble>
      </div>
    );
  }
  // "past" (lista do ano passado) está desativado no ponto de partida — ver
  // PapelariaIntro. Lista que já está nesse modo abre o montador normal, sem
  // preencher nada.
  return <PapelariaBuilder onReset={() => setData(d => ({ ...d, papelariaMode: undefined }))} />;
}

// ================================================================
// Screen 1, intro
// ================================================================
function PapelariaIntro({ onPick, currentMode }) {
  return (
    <div>
      {/* Header compacto com Lex Papelaria à esquerda */}
      <PremiumHeader
        image="assets/lex-papelaria-bg.png"
        imageAlt="Lex na papelaria"
        eyebrow="Papelaria · Passo 1, Ponto de partida"
        title="Como você quer começar a lista de papelaria?"
        subtitle="Papelaria entra como bloco separado no PDF das famílias. Você pode pular e fazer só literatura+didáticos, ou montar agora, sem sair do Scolist."
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginTop: 28 }}>
        {[
          { id: "skip",    icon: "📚", title: "Só livros", sub: "Pular esta etapa. A lista final não terá papelaria." },
          // Desativado até existir a versão de verdade: era um mock que
          // gerava uma lista a partir do catálogo e SUBSTITUÍA a papelaria de
          // todas as séries, sem confirmação (Eleva Recife, 23/10/2026 22:53).
          { id: "past",    icon: "🗂️", title: "Usar lista do ano passado", sub: "Em breve: puxar a papelaria do ano anterior do Scolist.", disabled: true },
          // "Usar lista média do mercado" (market) saiu em 26/10/2026: a
          // "média" vinha do catálogo mock de papelaria. Lista salva nesse
          // modo abre o montador normal (ver StepPapelariaBuilder).
          { id: "scratch", icon: "✏️",  title: "Montar a lista", sub: "Busca os itens na loja online, com preço e estoque de verdade. Você monta série a série." },
        ].map(opt => {
          const selected = opt.id === currentMode;
          const off = !!opt.disabled;
          return (
            <button key={opt.id} onClick={() => { if (!off) onPick(opt.id); }} disabled={off} aria-disabled={off}
              style={{
                padding: 20, borderRadius: 18,
                background: off ? "var(--bg-light)" : "#fff",
                border: `1px solid ${selected && !off ? "var(--teal)" : "var(--border)"}`,
                boxShadow: selected && !off ? "0 0 0 3px rgba(47,151,161,0.15)" : off ? "none" : "var(--shadow-sm)",
                textAlign: "left",
                cursor: off ? "not-allowed" : "pointer",
                opacity: off ? 0.6 : 1,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { if (!selected && !off) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.borderColor = "var(--teal)"; } }}
              onMouseLeave={(e) => { if (!selected && !off) { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; e.currentTarget.style.borderColor = "var(--border)"; } }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontSize: 30, marginBottom: 10 }}>{opt.icon}</div>
                {off
                  ? <div style={{ padding: "3px 8px", background: "var(--border)", color: "var(--text-mute)", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>EM BREVE</div>
                  : selected && <div style={{ padding: "3px 8px", background: "var(--teal)", color: "#fff", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>SELECIONADO</div>}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>{opt.title}</div>
              <div style={{ fontSize: 12, color: "var(--text-mute)", lineHeight: 1.45 }}>{opt.sub}</div>
            </button>
          );
        })}
      </div>

      {currentMode && currentMode !== "skip" && (
        <div style={{ marginTop: 24, padding: 14, background: "var(--bg-light)", borderRadius: 12, fontSize: 13, color: "var(--text-mute)", display: "flex", alignItems: "center", gap: 10 }}>
          → Avance pro próximo passo (<strong>Montagem da lista</strong>) pra começar.
        </div>
      )}

    </div>
  );
}

// ================================================================
// Screen 1 alt, skipped confirmation
// ================================================================
function PapelariaSkipped({ onReopen }) {
  return (
    <div>
      <Heading
        eyebrow="Papelaria · Passo 1, Ponto de partida"
        title="Você optou por não incluir papelaria"
        subtitle="Tudo certo, a lista final terá só literatura. Pode voltar atrás a qualquer momento." />
      <LexBubble pose="wave">Beleza! Seguindo direto pra confirmação e geração do link.</LexBubble>
      <div style={{ marginTop: 24 }}>
        <BtnGhost onClick={onReopen}>← Mudei de ideia, quero incluir papelaria</BtnGhost>
      </div>
    </div>
  );
}

// ================================================================
// Logo helper, tenta <img> e cai pra placeholder colorido se 404
// ================================================================
function PartnerLogo({ partner, h = 32 }) {
  const [failed, setFailed] = useState(false);
  if (failed || !partner.logo) {
    return (
      <div style={{
        height: h, minWidth: h * 2, padding: "0 10px",
        background: partner.color, color: "#fff",
        borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: h * 0.38, fontWeight: 800, letterSpacing: 0.5,
      }}>{partner.name}</div>
    );
  }
  return <img src={partner.logo} alt={partner.name} onError={() => setFailed(true)}
    style={{ height: h, objectFit: "contain", maxWidth: 140 }} />;
}

// ================================================================
// Screen 2, builder (search + cart per series)
// ================================================================
function PapelariaBuilder({ onReset }) {
  const { data, setData, account } = useApp();
  // Séries de verdade da escola (Passo 1), não a lista fixa de 12.
  const activeSeries = resolveSeriesList(data);
  const [seriesIdx, setSeriesIdx] = useState(0);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [liveCatalogVersion, setLiveCatalogVersion] = useState(0);

  // Busca de verdade no estoque VTEX (mesmo endpoint do dashboard/scolist
  // antigo, GET /searchBySeller) — resultados são normalizados pro formato de
  // PAPELARIA_CATALOG e empurrados pra lá, igual o padrão já usado pra busca
  // de livros em steps-curation.jsx. Vale pra qualquer modo salvo: "past" e
  // "market" eram mocks e saíram (lista nesses modos monta igual).
  useEffect(() => {
    if (query.trim().length < 2) return undefined;
    if (!window.ScolistAPI || typeof window.ScolistAPI.searchStationeryVtex !== "function") return undefined;

    let cancelled = false;
    const sellers = (account && account.school && account.school.sellerRestrictions) || [];

    const handle = setTimeout(async () => {
      try {
        const liveItems = await window.ScolistAPI.searchStationeryVtex(query, sellers);
        if (cancelled) return;
        liveItems.forEach((item) => {
          const existingIdx = PAPELARIA_CATALOG.findIndex((c) => c.id === item.id);
          if (existingIdx >= 0) PAPELARIA_CATALOG[existingIdx] = item;
          else PAPELARIA_CATALOG.push(item);
        });
        if (liveItems.length) setLiveCatalogVersion((n) => n + 1);
      } catch (err) {
        console.error("Falha ao buscar papelaria na VTEX:", err);
      }
    }, 350);

    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, account]);

  const papelaria = data.papelaria || {}; // { [seriesIdx]: [{itemId, brandIdx, qty}] }
  const currentCart = papelaria[seriesIdx] || [];

  const setCart = (newCart) => {
    setData(d => ({ ...d, papelaria: { ...(d.papelaria || {}), [seriesIdx]: newCart } }));
  };

  // Filtra o que já veio da VTEX (busca + categoria). Nada aparece até o
  // usuário buscar ou escolher uma categoria.
  const results = useMemo(() => {
    const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const tokens = norm(query.trim()).split(/\s+/).filter(Boolean);
    if (tokens.length === 0 && !categoryFilter) return [];
    return PAPELARIA_CATALOG.filter(item => {
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (tokens.length === 0) return true;
      // Haystack: nome + categoria + todas as marcas/modelos
      const hay = norm([
        item.genericName,
        item.category,
        ...item.brands.flatMap(b => [PAPELARIA_PARTNERS[b.partnerId]?.name || "", b.model]),
      ].join(" "));
      // Todas as palavras têm que casar (AND)
      return tokens.every(t => hay.includes(t));
    });
  }, [query, categoryFilter, liveCatalogVersion]);

  const categories = useMemo(() => {
    const s = new Set(PAPELARIA_CATALOG.map(x => x.category));
    return Array.from(s);
  }, [liveCatalogVersion]);

  const addToCart = (itemId, brandIdx = 0) => {
    const item = PAPELARIA_CATALOG.find(x => x.id === itemId);
    const defaultQty = item?.qty?.avg || 1;
    const existing = currentCart.find(c => c.itemId === itemId);
    if (existing) {
      setCart(currentCart.map(c => c.itemId === itemId ? { ...c, brandIdx } : c));
    } else {
      setCart([...currentCart, { itemId, brandIdx, qty: defaultQty }]);
    }
  };

  const removeFromCart = (itemId) => {
    setCart(currentCart.filter(c => c.itemId !== itemId));
  };

  const updateQty = (itemId, qty) => {
    setCart(currentCart.map(c => c.itemId === itemId ? { ...c, qty: Math.max(0, qty) } : c));
  };

  // Kit (opcional): "escola" = fica na escola, "mochila" = vai na mochila.
  // Clicar de novo na opção já marcada desmarca (volta a undefined).
  // Sem `{ kit, ...rest } = c`: o Babel standalone compila o rest com um
  // `_excluded` global que outro arquivo sobrescreve, e o kit nunca saía.
  const updateKit = (itemId, kit) => {
    setCart(currentCart.map(c => {
      if (c.itemId !== itemId) return c;
      const next = { ...c };
      if (c.kit === kit) delete next.kit;
      else next.kit = kit;
      return next;
    }));
  };

  // Item salvo no carrinho sem cadastro no catálogo (nem estático nem o
  // snapshot "vtex-" do progresso): resto de antes do fix de 18/09 (ver
  // snapshotRuntimePapelariaCatalog em app.jsx), copiado também para as
  // outras unidades Eleva. Antes ele contava no "N itens" mas não aparecia
  // em lugar nenhum (nem aqui, nem na planilha, nem pra família) — a escola
  // via 50 e contava 46 (Eleva Barra, 19/10/2026). Agora conta só o que
  // existe e os órfãos aparecem à parte, com botão pra remover.
  const cartResolved = currentCart.filter(c => PAPELARIA_CATALOG.some(x => x.id === c.itemId));
  const cartOrphans = currentCart.filter(c => !PAPELARIA_CATALOG.some(x => x.id === c.itemId));
  const totalItems = cartResolved.reduce((s, c) => s + c.qty, 0);
  const totalCost = currentCart.reduce((s, c) => {
    const it = PAPELARIA_CATALOG.find(x => x.id === c.itemId);
    const br = it?.brands[c.brandIdx];
    return s + (br?.price || 0) * c.qty;
  }, 0);

  return (
    <div>
      <PremiumHeader
        image="assets/lex-papelaria-bg.png"
        imageAlt="Lex Papelaria"
        eyebrow="Papelaria · Passo 1, Montagem"
        title="Monte a papelaria"
        subtitle="Cada série tem sua lista. Os itens vêm da loja online, com o preço de lá."
      />

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, marginBottom: 16 }}>
        <BtnGhost onClick={onReset}>← Trocar modo de início</BtnGhost>
      </div>

      {/* Series tabs */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 18 }}>
        {activeSeries.map((s, i) => {
          const seriesCart = (papelaria[i] || []);
          // Só o que tem cadastro (ver cartOrphans abaixo).
          const itemCount = seriesCart.filter(c => PAPELARIA_CATALOG.some(x => x.id === c.itemId)).length;
          return (
            <button key={s} onClick={() => setSeriesIdx(i)} style={{
              padding: "8px 14px", borderRadius: 10,
              background: i === seriesIdx ? "var(--teal)" : "transparent",
              color: i === seriesIdx ? "#fff" : "var(--text)",
              border: `1px solid ${i === seriesIdx ? "var(--teal)" : "var(--border)"}`,
              fontSize: 13, fontWeight: i === seriesIdx ? 700 : 500,
              display: "inline-flex", alignItems: "center", gap: 7,
            }}>
              {s}
              {itemCount > 0 && <span style={{
                padding: "1px 7px", fontSize: 10.5, fontWeight: 700, borderRadius: 9,
                background: i === seriesIdx ? "rgba(255,255,255,0.3)" : "var(--green)",
                color: i === seriesIdx ? "#fff" : "var(--text)",
              }}>{itemCount}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 24, alignItems: "start" }}>
        {/* ========== LEFT: SEARCH + RESULTS ========== */}
        <div>
          {/* Search bar */}
          <div style={{
            display: "flex", gap: 10, alignItems: "center", marginBottom: 14,
            background: "#fff", padding: "10px 14px", borderRadius: 12,
            border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)",
          }}>
            <span style={{ fontSize: 18 }}>🔍</span>
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar: caderno, lápis de cor, tesoura…"
              style={{
                flex: 1, border: "none", outline: "none", fontSize: 15,
                background: "transparent", color: "var(--text)",
              }} />
            {query && <button onClick={() => setQuery("")} style={{ fontSize: 12, color: "var(--text-mute)" }}>limpar</button>}
          </div>

          {/* Category chips */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            <button onClick={() => setCategoryFilter(null)} style={chipStyle(categoryFilter === null)}>
              Todos
            </button>
            {categories.map(c => (
              <button key={c} onClick={() => setCategoryFilter(c === categoryFilter ? null : c)}
                style={chipStyle(categoryFilter === c)}>
                {c}
              </button>
            ))}
          </div>

          {/* Results */}
          <div style={{ display: "grid", gap: 10 }}>
            {results.length === 0 && !query && !categoryFilter && (
              <div style={{ padding: "60px 40px", textAlign: "center", background: "#fff", borderRadius: 14, border: "1px dashed var(--border)" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>
                  Comece buscando um item
                </div>
                <div style={{ fontSize: 13, color: "var(--text-mute)", maxWidth: 360, margin: "0 auto", lineHeight: 1.5 }}>
                  Digite algo como <strong>"caderno"</strong>, <strong>"lápis de cor"</strong> ou <strong>"tesoura"</strong>,
                  <br/>ou escolha uma categoria acima.
                </div>
              </div>
            )}
            {results.length === 0 && (query || categoryFilter) && (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text-mute)", fontSize: 14 }}>
                Nenhum item encontrado{query && <> pra "<strong>{query}</strong>"</>}.
                <div style={{ fontSize: 12, marginTop: 8 }}>Tente termos mais genéricos como "caneta" ou "pasta".</div>
              </div>
            )}
            {results.map(item => (
              <ItemCard key={item.id} item={item}
                inCart={currentCart.find(c => c.itemId === item.id)}
                onAdd={(brandIdx) => addToCart(item.id, brandIdx)}
                onRemove={() => removeFromCart(item.id)}
              />
            ))}
          </div>
        </div>

        {/* ========== RIGHT: CART ========== */}
        <div style={{
          position: "sticky", top: 96,
          background: "#fff", borderRadius: 16, border: "1px solid var(--border)",
          boxShadow: "var(--shadow-sm)", padding: 18,
        }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--teal-dark)", textTransform: "uppercase", letterSpacing: 1 }}>
              {activeSeries[seriesIdx]}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-mute)" }}>
              {cartResolved.length} item{cartResolved.length !== 1 ? "s" : ""}
            </div>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 14 }}>Lista desta série</div>

          {cartOrphans.length > 0 && (
            <div style={{
              marginBottom: 12, padding: "10px 12px", borderRadius: 10,
              background: "#fff8ec", border: "1px solid #f3d9a8",
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#8a5a00", marginBottom: 4 }}>
                ⚠️ {cartOrphans.length} {cartOrphans.length === 1 ? "item sem cadastro" : "itens sem cadastro"}
              </div>
              <div style={{ fontSize: 11, color: "#7a5a1e", lineHeight: 1.45, marginBottom: 8 }}>
                {cartOrphans.length === 1 ? "Está salvo nesta série, mas não" : "Estão salvos nesta série, mas não"} aparece{cartOrphans.length === 1 ? "" : "m"} pras famílias nem na planilha. Remova e, se precisar, busque o item de novo.
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                {cartOrphans.map(c => (
                  <div key={c.itemId} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "#5c4413" }}>
                    <span style={{ flex: 1, minWidth: 0, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.itemId} · {c.qty || 1} un.
                    </span>
                    <button onClick={() => removeFromCart(c.itemId)} style={{
                      fontSize: 11, fontWeight: 700, color: "#a4442a", padding: "2px 8px",
                      border: "1px solid #e8b9a8", borderRadius: 6, background: "#fff",
                    }}>Remover</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cartResolved.length === 0 && cartOrphans.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "var(--text-mute)", fontSize: 12.5, border: "1px dashed var(--border)", borderRadius: 10 }}>
              Nenhum item ainda.
              <br />Busque e adicione itens ao lado.
            </div>
          )}

          <div style={{ display: "grid", gap: 8, maxHeight: 480, overflowY: "auto" }}>
            {currentCart.map(c => {
              const item = PAPELARIA_CATALOG.find(x => x.id === c.itemId);
              if (!item) return null;
              const brand = item.brands[c.brandIdx];
              const partner = PAPELARIA_PARTNERS[brand.partnerId];
              const forbidden = !!item.forbidden;
              return (
                <div key={c.itemId} style={{
                  padding: "10px 12px",
                  background: forbidden ? "#fff4f4" : "var(--bg-light)",
                  borderRadius: 10,
                  border: `1px solid ${forbidden ? "#f5c5c5" : "transparent"}`,
                }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", lineHeight: 1.3, marginBottom: 3 }}>
                        {forbidden && <span style={{ color: "#c53030", marginRight: 4 }}>⚠️</span>}
                        {item.genericName}
                      </div>
                      <div style={{ fontSize: 10.5, color: "var(--text-mute)" }}>
                        {partner?.name} · {brand.model} · R$ {brand.price.toFixed(2)}
                      </div>
                    </div>
                    <button onClick={() => removeFromCart(c.itemId)}
                      style={{ fontSize: 14, color: "var(--text-mute)", padding: 2 }}
                      title="Remover">✕</button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <button onClick={() => (c.qty <= 1 ? removeFromCart(c.itemId) : updateQty(c.itemId, c.qty - 1))} style={qtyBtnStyle}>−</button>
                    <input type="number" value={c.qty} min={0}
                      onChange={(e) => updateQty(c.itemId, parseInt(e.target.value) || 0)}
                      onBlur={(e) => { if ((parseInt(e.target.value) || 0) <= 0) removeFromCart(c.itemId); }}
                      style={{
                        width: 46, padding: "3px 6px", textAlign: "center",
                        border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, fontWeight: 700,
                      }} />
                    <button onClick={() => updateQty(c.itemId, c.qty + 1)} style={qtyBtnStyle}>+</button>
                    <span style={{ fontSize: 10.5, color: "var(--text-mute)" }}>{item.unit}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                    <span style={{ fontSize: 10.5, color: "var(--text-mute)" }}>Kit <span style={{ opacity: 0.7 }}>(opcional)</span>:</span>
                    {KIT_OPTIONS.map(k => (
                      <button key={k.id} onClick={() => updateKit(c.itemId, k.id)}
                        title={c.kit === k.id ? "Clique pra desmarcar" : `Marcar como ${k.label}`}
                        style={kitBtnStyle(c.kit === k.id)}>
                        {k.icon} {k.label}
                      </button>
                    ))}
                  </div>
                  {forbidden && (
                    <div style={{ marginTop: 6, fontSize: 10.5, color: "#c53030", lineHeight: 1.4 }}>
                      <strong>Proibido:</strong> {item.forbidden.reason}
                      <div style={{ fontSize: 9.5, color: "#999", marginTop: 2 }}>
                        {item.forbidden.laws.join(" · ")}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {cartResolved.length > 0 && (
            <>
              <div style={{ height: 1, background: "var(--border)", margin: "14px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-mute)", marginBottom: 4 }}>
                <span>Total de itens</span><span>{totalItems}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, color: "var(--teal-dark)" }}>
                <span>Estimativa</span><span>R$ {totalCost.toFixed(2)}</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--text-mute)", marginTop: 3 }}>
                preços de referência, cobrados pelo parceiro na compra
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// Item card, one genericName expanded with brand tiles
// ================================================================
function ItemCard({ item, inCart, onAdd, onRemove }) {
  const forbidden = !!item.forbidden;
  const selectedBrandIdx = inCart?.brandIdx ?? 0;
  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      border: `1px solid ${forbidden ? "#f5c5c5" : inCart ? "var(--teal)" : "var(--border)"}`,
      padding: 14, position: "relative",
      boxShadow: inCart ? "0 0 0 1px var(--teal)" : "var(--shadow-sm)",
    }}>
      {/* Header */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              {forbidden && <span style={{ marginRight: 6 }}>🚫</span>}
              {item.genericName}
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-mute)" }}>
            {item.category}
          </div>
        </div>
        {inCart && (
          <div style={{
            padding: "4px 10px", borderRadius: 12, background: "var(--teal)", color: "#fff",
            fontSize: 11, fontWeight: 700,
          }}>✓ na lista</div>
        )}
      </div>

      {/* Forbidden banner */}
      {forbidden && (
        <div style={{
          padding: "10px 12px", background: "#fff4f4", border: "1px solid #f5c5c5",
          borderRadius: 8, marginBottom: 10,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#c53030", marginBottom: 4 }}>
            ⚠️ Item geralmente proibido em lista escolar
          </div>
          <div style={{ fontSize: 11.5, color: "#7a1a1a", lineHeight: 1.45 }}>
            {item.forbidden.reason}
          </div>
          <div style={{ fontSize: 10, color: "#999", marginTop: 4 }}>
            {item.forbidden.laws.join(" · ")}
          </div>
        </div>
      )}

      {/* Brand tiles */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${item.brands.length}, 1fr)`, gap: 8 }}>
        {item.brands.map((b, i) => {
          const partner = PAPELARIA_PARTNERS[b.partnerId];
          const active = inCart && selectedBrandIdx === i;
          return (
            <button key={i} onClick={() => onAdd(i)} disabled={forbidden}
              style={{
                padding: 10, borderRadius: 10, textAlign: "left",
                background: active ? "rgba(47,151,161,0.06)" : "#fff",
                border: `1px solid ${active ? "var(--teal)" : "var(--border)"}`,
                cursor: forbidden ? "not-allowed" : "pointer",
                opacity: forbidden ? 0.5 : 1,
                transition: "all 0.12s",
              }}
              onMouseEnter={(e) => { if (!forbidden && !active) e.currentTarget.style.borderColor = "var(--teal)"; }}
              onMouseLeave={(e) => { if (!forbidden && !active) e.currentTarget.style.borderColor = "var(--border)"; }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <PartnerLogo partner={partner} h={18} />
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text)", fontWeight: 600, lineHeight: 1.3, marginBottom: 4 }}>
                {b.model}
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "var(--teal-dark)" }}>
                R$ {b.price.toFixed(2)}
              </div>
            </button>
          );
        })}
      </div>

      {inCart && (
        <button onClick={onRemove}
          style={{ marginTop: 10, fontSize: 11.5, color: "#c53030", fontWeight: 600 }}>
          Remover da lista
        </button>
      )}
    </div>
  );
}

// ================================================================
// Styles helpers
// ================================================================
const chipStyle = (active) => ({
  padding: "5px 12px", borderRadius: 20,
  background: active ? "var(--teal)" : "#fff",
  color: active ? "#fff" : "var(--text)",
  border: `1px solid ${active ? "var(--teal)" : "var(--border)"}`,
  fontSize: 12, fontWeight: active ? 700 : 500,
  cursor: "pointer",
});
const KIT_OPTIONS = [
  { id: "escola",  label: "Kit escola",  icon: "🏫" },
  { id: "mochila", label: "Kit mochila", icon: "🎒" },
];

const kitBtnStyle = (active) => ({
  padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: active ? 700 : 500,
  background: active ? "var(--teal)" : "#fff",
  color: active ? "#fff" : "var(--text)",
  border: `1px solid ${active ? "var(--teal)" : "var(--border)"}`,
  cursor: "pointer",
});

const qtyBtnStyle = {
  width: 22, height: 22, borderRadius: 6,
  background: "#fff", border: "1px solid var(--border)",
  fontSize: 14, fontWeight: 700, color: "var(--teal-dark)",
  display: "flex", alignItems: "center", justifyContent: "center",
};

Object.assign(window, {
  StepPapelariaIntro,
  StepPapelariaBuilder,
});
